import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as layoutTemplateActions from '@redux/modules/layoutTemplate'

import defaultFormState, { requiredFields } from '@models/layoutTemplate'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { LayoutTemplateRequestOptions } from '@domain-group/realhub-backend-api-types'
import type { LayoutTemplateModel } from '@models/layoutTemplate'
import mergeHashQueryParamsWithRedirect from '@functions/mergeHashQueryParamsWithRedirect'

export const generateUrls = (
  layoutTemplate: Partial<LayoutTemplateModel> = {},
  options: { includeRedirect?: boolean } = {},
) => {
  const { includeRedirect } = options
  const { id } = layoutTemplate

  return {
    archivedLayoutTemplatesIndexUrl: mergeHashQueryParamsWithRedirect({
      hash: '#/layoutTemplates?hidden=true',
      includeRedirect,
    }),
    layoutTemplatesIndexUrl: mergeHashQueryParamsWithRedirect({
      hash: '#/layoutTemplates?hidden=false',
      includeRedirect,
    }),
  }
}

type CreateLayoutTemplateParams = {
  dispatch: AppDispatch,
  layoutTemplateParams: Partial<LayoutTemplateModel>,
  requestOptions?: LayoutTemplateRequestOptions,
}

const createLayoutTemplate = (params: CreateLayoutTemplateParams) => {
  const { dispatch, layoutTemplateParams, requestOptions } = params
  const { createLayoutTemplate: createFn } = layoutTemplateActions

  return dispatch(createFn(layoutTemplateParams, requestOptions))
}

type UpdateLayoutTemplateParams = {
  dispatch: AppDispatch,
  layoutTemplate: LayoutTemplateModel,
  layoutTemplateParams: Partial<LayoutTemplateModel>,
  requestOptions?: LayoutTemplateRequestOptions,
}

const updateLayoutTemplate = (params: UpdateLayoutTemplateParams) => {
  const {
    dispatch, layoutTemplate, layoutTemplateParams, requestOptions,
  } = params
  const { updateLayoutTemplate: updateFn } = layoutTemplateActions

  const updatedParams = {
    id: layoutTemplate.id,
    ...layoutTemplateParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteLayoutTemplateParams = {
  dispatch: AppDispatch,
  layoutTemplate: DeleteParams<LayoutTemplateModel>,
}

const deleteLayoutTemplate = (params: DeleteLayoutTemplateParams) => {
  const { dispatch, layoutTemplate } = params
  const { deleteLayoutTemplate: deleteFn } = layoutTemplateActions

  return dispatch(deleteFn(layoutTemplate))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

type DuplicateLayoutTemplateParams = {
  dispatch: AppDispatch,
  layoutTemplate: LayoutTemplateModel,
}

const duplicateLayoutTemplate = (params: DuplicateLayoutTemplateParams) => {
  const { dispatch, layoutTemplate } = params
  const { duplicateLayoutTemplate: duplicateFn } = layoutTemplateActions

  return dispatch(duplicateFn(layoutTemplate))
}

type ToggleLayoutTemplateVisibilityParams = {
  dispatch: AppDispatch,
  layoutTemplate: LayoutTemplateModel,
}

const toggleLayoutTemplateVisibility = (params: ToggleLayoutTemplateVisibilityParams) => {
  const { dispatch, layoutTemplate } = params
  const { toggleLayoutTemplateVisibility: toggleFn } = layoutTemplateActions

  return dispatch(toggleFn(layoutTemplate))
}

export function useLayoutTemplateForm(
  layoutTemplate: Partial<LayoutTemplateModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const layoutTemplateForm = useForm(
    defaultFormState,
    { entity: layoutTemplate, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [layoutTemplate.id, layoutTemplate.cache_key],
  )

  return {
    ...layoutTemplateForm,
  }
}

type UpdateCompatibleProducts = {
  dispatch: AppDispatch,
  layoutTemplate: LayoutTemplateModel,
  entityParams: number[],
}

const updateCompatibleProducts = (params: UpdateCompatibleProducts) => {
  const {
    dispatch, entityParams, layoutTemplate,
  } = params

  const { updateCompatibleProducts: updateProductsFn } = layoutTemplateActions

  // @ts-expect-error TS2339
  return dispatch(updateProductsFn(layoutTemplate, entityParams?.publication_product_ids, {
    include_layout_template_compatible_products: true,
    include_layout_template_publications: true,
  }))
}

export const useRelations = (layoutTemplate: Partial<LayoutTemplateModel> = {}) => {
  const { designer_id } = layoutTemplate

  const entities = useSelector(reduxState => reduxState.entities)
  const { providers } = entities

  // @ts-expect-error TS2538
  const provider = providers[designer_id] || {}

  return {
    provider,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: LayoutTemplateRequestOptions,
}

function useLayoutTemplate(initEntity: Partial<LayoutTemplateModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: layoutTemplate }: { entity: LayoutTemplateModel} = useLatestEntity(initEntity, 'layoutTemplates')
  const { id } = layoutTemplate

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { provider } = useRelations(layoutTemplate)

  const compatibleProducts = layoutTemplate?.compatible_products ?? []
  // @ts-expect-error
  const compatiblePublications = layoutTemplate?.publications ?? []

  const createdByProvider = layoutTemplate.designer_type === 'Provider'

  useReduxAction(
    'layoutTemplates',
    'loadLayoutTemplate',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.layoutTemplates)

  return {
    callbacks: {
      assignLayoutTemplateProducts: (stepKey: string) => launchModal({
        callbacks,
        modalKey: 'AssignLayoutTemplatePermissionsModal',
        payload: {
          stepKey,
          layoutTemplate,
        },
      }),
      // @ts-expect-error TS7006
      updateCompatibleProducts: entityParams => (
        updateCompatibleProducts({
          dispatch,
          entityParams,
          layoutTemplate,
        })
      ),
      createLayoutTemplate: (layoutTemplateParams: Partial<LayoutTemplateModel>, entityOptions?: LayoutTemplateRequestOptions) => (
        createLayoutTemplate({ layoutTemplateParams, dispatch, requestOptions: entityOptions })
      ),
      duplicateLayoutTemplate: () => duplicateLayoutTemplate({ dispatch, layoutTemplate }),
      toggleLayoutTemplateVisibility: () => toggleLayoutTemplateVisibility({ dispatch, layoutTemplate }),
      createOrEditLayoutTemplate: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditLayoutTemplateModal',
        payload: { layoutTemplate },
      }),
      deleteLayoutTemplate: () => deleteLayoutTemplate({ dispatch, layoutTemplate }),
      updateLayoutTemplate: (layoutTemplateParams: Partial<LayoutTemplateModel>, entityOptions?: LayoutTemplateRequestOptions) => (
        updateLayoutTemplate({
          layoutTemplate, layoutTemplateParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    compatibleProductsCount: compatibleProducts.length,
    compatiblePublicationsCount: compatiblePublications.length,
    creating,
    deleting,
    layoutTemplate,
    createdByProvider,
    loading,
    provider,
    updating,
    urls: generateUrls(layoutTemplate),
  }
}

export default useLayoutTemplate
